import { ChangeEvent, useRef, useState } from 'react';

import { Button, InfoBox, TextField } from '@selina-finance/ui';
import cookie from 'cookie';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';

import { getFormHelpers, getValidationSchema } from '@Shared/helpers/input';
import { AuthErrorType } from '@src/app/contexts/Auth';
import { formItems } from '@src/app/formModels/auth/signIn';

export interface SignInViewProps {
    className?: string;
    requestOTP: Function;
    confirmOTP: Function;
    email: string;
    applicationId: string;
    confirmView: boolean;
    setConfirmView: (value: boolean) => void;
}

const SignInView = ({
    className,
    requestOTP,
    confirmOTP,
    email,
    applicationId,
    confirmView,
    setConfirmView,
}: SignInViewProps) => {
    const [session, setSession] = useState();
    const [error, setError] = useState<{
        incorrectCode?: string;
        cantConfirm?: boolean;
    }>({});

    const navigate = useNavigate();
    const formikRef = useRef<any>(null);

    const validationSchema = getValidationSchema(formItems(confirmView));

    const cookies = cookie.parse(document.cookie);
    const appInfo = cookies.application ? JSON.parse(cookies.application) : {};

    const onSubmitHandler = async () => {
        const values = formikRef.current.values;

        if (!confirmView) {
            const result = await requestOTP(values.email);
            if (result.success) {
                setSession(result.session);
            }
            setConfirmView(true);
        } else {
            const emailFromCookie = appInfo.emailAddress;
            let appID = applicationId || appInfo.applicationId;

            if (emailFromCookie !== values.email) {
                appID = null;
            }

            const result = await confirmOTP(
                values.email,
                values.otp,
                appID,
                session,
            );
            if (result.success) {
                navigate('/');
            } else if (!result.success) {
                if (result.errorType === AuthErrorType.INCORRECT_CODE) {
                    setSession(result.session);
                    setError({ incorrectCode: result.message });
                } else if (result.errorType === AuthErrorType.CODE_EXPIRED) {
                    setError({ incorrectCode: result.message });
                } else {
                    setError({ cantConfirm: true });
                    setConfirmView(false);
                }
            }
        }
    };

    const handleResendCode = async () => {
        const values = formikRef.current.values;

        const result = await requestOTP(values.email);
        if (result.success) {
            setSession(result.session);
            setError({ incorrectCode: undefined });
            formikRef.current?.setFieldValue('otp', '', false);
        }
    };

    return (
        <div className={className}>
            <Formik
                enableReinitialize
                initialValues={{ email: email, otp: '' }}
                validationSchema={validationSchema}
                onSubmit={onSubmitHandler}
                innerRef={(ref) => (formikRef.current = ref)}
            >
                {(formikProps) => {
                    const { getError } = getFormHelpers(
                        formItems(confirmView),
                        formikProps,
                    );

                    return (
                        <Form className='leading-120%'>
                            {error.cantConfirm ? (
                                <p>
                                    Sorry you’re experiencing difficulties
                                    logging in. Please contact us at&nbsp;
                                    <a
                                        href='mailto:support@selinafinance.co.uk'
                                        className='underline'
                                    >
                                        support@selinafinance.co.uk
                                    </a>{' '}
                                    or{' '}
                                    <span
                                        className='underline cursor-pointer'
                                        onClick={() =>
                                            window.Intercom?.('show')
                                        }
                                    >
                                        use the live chat
                                    </span>
                                    .
                                </p>
                            ) : (
                                <>
                                    {!confirmView && (
                                        <TextField
                                            id='email'
                                            name='email'
                                            className='mp-mask'
                                            labelSize='small'
                                            label='Email address'
                                            value={
                                                formikProps.values.email?.toString() ||
                                                ''
                                            }
                                            onChange={(
                                                e: ChangeEvent<HTMLInputElement>,
                                            ) => formikProps.handleChange(e)}
                                            onBlur={formikProps.handleBlur}
                                            error={getError('email')}
                                        />
                                    )}
                                    {confirmView && (
                                        <>
                                            <p className='mb-6'>
                                                If an account is linked to this
                                                email, you'll receive a code
                                                shortly. Please enter it here to
                                                continue.
                                            </p>
                                            <TextField
                                                otp
                                                id='otp'
                                                name='otp'
                                                maxLength={6}
                                                value={
                                                    formikProps.values.otp?.toString() ||
                                                    ''
                                                }
                                                onOtpChange={(val) => {
                                                    formikProps.setFieldValue(
                                                        'otp',
                                                        val,
                                                    );

                                                    if (error.incorrectCode) {
                                                        setError((prev) => ({
                                                            ...prev,
                                                            incorrectCode:
                                                                undefined,
                                                        }));
                                                    }
                                                }}
                                                onBlur={formikProps.handleBlur}
                                                error={
                                                    getError('otp') ||
                                                    error.incorrectCode
                                                }
                                            />
                                            <p className='mt-6'>
                                                Not received a code? Please wait
                                                a couple of minutes and be sure
                                                to check your junk/spam folder.
                                                If you still have not received a
                                                code,{' '}
                                                <span
                                                    className='underline cursor-pointer'
                                                    onClick={handleResendCode}
                                                >
                                                    click here to send another
                                                </span>
                                                .
                                            </p>

                                            {error.incorrectCode && (
                                                <InfoBox
                                                    className='pt-10 font-bold border-none'
                                                    type='error'
                                                >
                                                    {error.incorrectCode}
                                                </InfoBox>
                                            )}
                                        </>
                                    )}

                                    <Button
                                        type='submit'
                                        className='my-6 w-full'
                                        size='large'
                                        color='secondary'
                                    >
                                        {confirmView ? 'Log in' : 'Continue'}
                                    </Button>

                                    {!confirmView && (
                                        <p className='text-md mb-4 text-center'>
                                            Don’t have an account? &nbsp;
                                            <a
                                                href={
                                                    window?._env_
                                                        ?.NEWQUOTE_BASE_URL ||
                                                    ''
                                                }
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className='underline cursor-pointer text-md text-periwinkle-700 hover:text-periwinkle-800'
                                            >
                                                Get a quote and sign up.
                                            </a>
                                        </p>
                                    )}
                                </>
                            )}
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default SignInView;
